const solver = require('javascript-lp-solver');
import { countBy, sumBy } from 'lodash-es';


export enum EquipmentType {
  Unknown = 'unknown',
  Bar = 'bar' /** bar and plates */,
  Plates = 'plates' /** just the plates, no bar */,
  Stack = 'stack' /** stacked weights (in a machine) */,
  Fixed = 'fixed' /** fixed weights (dumbbells, or kettlebells) */,
  Manual = 'manual',
}
/** Definition of possible weights to use for an exercise. */
export interface EquipmentBase {
  id: string /** Unique id used to refer from exercises to configs, so configs can be shared. */;
  basedOnTemplateId?: string; /** Unique id of the template this config is based on. */
  name: string /** Name of the config as presented to the user. */;
  type: EquipmentType;
  weight_unit?: string;
}
export interface EquipmentUnknown extends EquipmentBase {
  type: EquipmentType.Unknown;
}
export interface EquipmentManual extends EquipmentBase {
  type: EquipmentType.Manual;
  weight_min_kg: number;
  weight_inc_kg: number;
}

export interface EquipmentElem_Plates {
  plates: {
    weight_kg: number;
  }[];
}

export interface EquipmentElem_Stack {
  stack: {
    totalWeight_kg: number;
  }[];
}

export interface EquipmentElem_Fixed {
  fixed: {
    weight_kg: number;
  }[];
}

export interface EquipmentPlates extends EquipmentBase, EquipmentElem_Plates {
  type: EquipmentType.Plates;
}

export interface EquipmentBar extends EquipmentBase, EquipmentElem_Plates {
  type: EquipmentType.Bar;
  bar_kg?: number /** Note: If bar is present, plates are loaded symmetrically! */;
  collar_kg?: number;
}

export interface EquipmentStack extends EquipmentBase, EquipmentElem_Stack, EquipmentElem_Plates {
  type: EquipmentType.Stack;
}

export interface EquipmentFixed extends EquipmentBase, EquipmentElem_Fixed, EquipmentElem_Plates {
  type: EquipmentType.Fixed;
}

export type Equipment =
  | EquipmentUnknown
  | EquipmentManual
  | EquipmentBar
  | EquipmentPlates
  | EquipmentStack
  | EquipmentFixed;

export interface WeightSetup {
  bar_kg?: number;
  collar_kg?: number;
  plates: {
    weight_kg: number;
  }[];
  fixedWeight_kg?: number;
  stackIndex?: number;
  baseWeight_kg?: number /** the minimum number for manual mode */;
  incrementWeight_kg?: number /** one increment for manual mode */;
  increments?: number /** number of increments for manual mode */;
  /** In manual mode, error specifies how much the weight is off from the best possible setup (always positive!). */
  error_kg?: number;
}

export function isEquipmentIdTemplate(configId: string): boolean {
  return typeof configId === 'string' && configId.startsWith('$');
}

export function isEquipmentValid(config: Equipment): boolean {
  if (!config) {
    return false;
  }
  if (
    config.type === EquipmentType.Manual &&
    isFinite(config.weight_inc_kg) &&
    isFinite(config.weight_min_kg) &&
    config.weight_inc_kg > 0 &&
    config.weight_min_kg >= 0
  ) {
    return true;
  } else if (
    config.type === EquipmentType.Plates &&
    config.plates &&
    config.plates.length > 0 &&
    config.plates.every(p => p.weight_kg > 0)
  ) {
    return true;
  } else if (
    config.type === EquipmentType.Bar &&
    config.plates &&
    config.plates.length > 0 &&
    config.plates.every(p => p.weight_kg > 0) &&
    config.bar_kg > 0
  ) {
    return true;
  } else if (
    // a repeated or out-of-order weight is harmless: adjustWeight picks exactly one stack entry and
    // getPossibleWeights keys its results by weight. Rejecting such a stack made every calculation
    // return the weight unchanged, which the app read as maxed-out equipment (#5860).
    config.type === EquipmentType.Stack &&
    config.stack &&
    config.stack.length > 0 &&
    config.stack.every(s => s.totalWeight_kg > 0)
  ) {
    return true;
  } else if (
    config.type === EquipmentType.Fixed &&
    config.fixed &&
    config.fixed.length > 0 &&
    config.fixed.every(f => f.weight_kg > 0)
  ) {
    return true;
  } else {
    return false;
  }
}

/**
 * How far a weight may sit off an equipment's loadable grid before the app calls it an error the
 * user should see. Anything smaller is the residue left by converting pounds to kilograms, or by
 * the float maths behind a synthesized increment, and is not a real mismatch. The same value
 * applies to every equipment type, which is why the name does not narrow it to one.
 */
export const WEIGHT_GRID_TOLERANCE_KG = 0.05;

/**
 * How many positions of a manual equipment the app is willing to work with. `getPossibleWeights`
 * enumerates this many, so a weight further up the grid than this is one the app cannot offer.
 */
export const MANUAL_EQUIPMENT_MAX_POSITIONS = 1000;

/** In 'manual' mode, how far the typed weight is off the best possible setup, or undefined when the gap is only sub-50g residue from lb conversion or synthesized-increment float math. */
function manualSetupError_kg(initialWeight_kg: number, bestWeight_kg: number): number | undefined {
  const error_kg = initialWeight_kg - bestWeight_kg;
  return Math.abs(error_kg) < WEIGHT_GRID_TOLERANCE_KG ? undefined : error_kg;
}

/** Adjust a weight to fit so it can be arranged with given plates. Increase or decrease weight by one step if needed, and round as desired. In 'manual' mode, it just finds how much it is off, but doesn't change the number. */
export function adjustWeight(
  initialWeight_kg: number,
  mode: 'step_down' | 'step_up' | 'round_down' | 'round' | 'manual',
  config: Equipment,
): { weight_kg: number; setup: WeightSetup } {
  /*
  Note: Internally, we treat 'manual' same as 'round_down' in all cases, just at the end, we don't change the actual weight in manual mode.
  */
  let orgMode = mode;
  if (mode === 'manual') {
    mode = 'round_down'; // I don't like mutating inputs either, but code below is more readable this way
  }
  if (isEquipmentValid(config) && (initialWeight_kg !== null && initialWeight_kg !== undefined)) {
    if (config.type === EquipmentType.Manual) {
      let step = (mode === 'step_down' && -1) || (mode === 'step_up' && +1) || 0;
      let newWeight = initialWeight_kg + step * config.weight_inc_kg;
      if (orgMode === 'manual') {
        // this prevents rounding errors that would cause 50 lbs to be snapped to 45 when using 5 lbs increments
        newWeight += 0.001;
      }

      let setup: WeightSetup = {
        plates: [],
        baseWeight_kg: config.weight_min_kg,
        incrementWeight_kg: config.weight_inc_kg,
        increments: 0,
      };

      if (mode === 'round_down') {
        setup.increments = Math.trunc((newWeight - config.weight_min_kg) / config.weight_inc_kg);
      } else {
        setup.increments = Math.round((newWeight - config.weight_min_kg) / config.weight_inc_kg);
      }
      setup.increments = Math.max(setup.increments, 0);

      newWeight = setup.increments * config.weight_inc_kg + config.weight_min_kg;

      if (orgMode === 'manual') {
        setup.error_kg = manualSetupError_kg(initialWeight_kg, newWeight);
        return { weight_kg: initialWeight_kg, setup };
      } else {
        return { weight_kg: newWeight, setup };
      }
    } else if (
      config.type === EquipmentType.Plates ||
      config.type === EquipmentType.Bar ||
      config.type == EquipmentType.Stack ||
      config.type == EquipmentType.Fixed
    ) {
      let hasABar = config.type === EquipmentType.Bar && (config as EquipmentBar);
      let stack = (config.type === EquipmentType.Stack && config.stack) || [{ totalWeight_kg: 0 }];
      let fixed = (config.type === EquipmentType.Fixed && config.fixed) || [{ weight_kg: 0 }];

      let hugeError = 100000;
      let barSidesMul = hasABar ? 2 : 1; // bar is loaded symmetrically, without it, load only one of each

      let model = {
        optimize: undefined,
        opType: undefined,
        constraints: {},
        variables: {},
        binaries: {},
        ints: {},
      };

      // this is the weight before adding the plates, or anything else
      let baseWeight_kg = 0;
      if (hasABar) {
        baseWeight_kg += (hasABar.bar_kg || 0) + (hasABar.collar_kg || 0) * 2;
      }

      /* We are trying to use as little plates as possible, however it is more important to match the requested weight as precisely as possible.

          Optimization goal is to minimize:

            cost = overshoot + undershoot + SUM(plate_used * plate_factor) 

          with constraints that:

            target_weight  = SUM(plate_weight * plate_used) + undershoot - overshoot

          Note that `plate_factor` is:
          1) so small that when all plates are used, the sum is still smaller than smallest possible relevant weight delta - which is 10 grams.
          2) is proportional to square root of weigth, so that 20+10 is the preferred combination over 15+15
          */

      model.optimize = 'cost';
      model.opType = 'min';

      for (let stackIndex = 0; stackIndex < stack.length; stackIndex++) {
        let stackVar = `stack_${stackIndex}`;
        let stackWeight_kg = stack[stackIndex].totalWeight_kg;
        model.binaries[stackVar] = 1;
        model.variables[stackVar] = { target_weight: stackWeight_kg, stackCount: 1 };
      }
      model.constraints['stackCount'] = { equal: 1 };

      for (let fixedIndex = 0; fixedIndex < fixed.length; fixedIndex++) {
        let fixedVar = `fixed_${fixedIndex}`;
        let fixedWeight_kg = fixed[fixedIndex].weight_kg;
        model.binaries[fixedVar] = 1;
        model.variables[fixedVar] = { target_weight: fixedWeight_kg, fixedCount: 1 };
      }
      model.constraints['fixedCount'] = { equal: 1 };

      let platesMap = countBy(config.plates, p => p.weight_kg);
      let plateSqrtTotal = sumBy(config.plates, p => Math.sqrt(p.weight_kg));
      for (let weight in platesMap) {
        let plateVar = `plate_${weight}`;
        let plateConstraint = `plate_${weight}_C`;
        let plateWeight_kg = parseFloat(weight);
        let plateFactor = 0.0001 * (Math.sqrt(plateWeight_kg) / plateSqrtTotal);

        model.ints[plateVar] = 1;
        model.variables[plateVar] = { target_weight: plateWeight_kg * barSidesMul, cost: plateFactor, [plateConstraint]: 1 };
        model.constraints[plateConstraint] = { max: platesMap[weight] };
      }

      if (mode === 'round') {
        model.constraints[`target_weight`] = { equal: initialWeight_kg - baseWeight_kg };
        model.variables['undershoot'] = { target_weight: +1, cost: 1 };
        model.variables['overshoot'] = { target_weight: -1, cost: 1 };
        //
      } else if (mode === 'step_down') {
        model.constraints[`target_weight`] = { equal: initialWeight_kg - baseWeight_kg - 0.001 };
        model.variables['undershoot'] = { target_weight: +1, cost: 1 };
        //model.variables['overshoot'] = { target_weight: -1, cost: 1 };
        //
      } else if (mode === 'step_up') {
        model.constraints[`target_weight`] = { equal: initialWeight_kg - baseWeight_kg + 0.001 };
        model.variables['undershoot'] = { target_weight: +1, cost: 100000 };
        model.variables['overshoot'] = { target_weight: -1, cost: 1 };
        //
      } else if (mode === 'round_down') {
        model.constraints[`target_weight`] = { equal: initialWeight_kg - baseWeight_kg + 0.001 };
        model.variables['undershoot'] = { target_weight: +1, cost: 1 };
        //model.variables['overshoot'] = { target_weight: -1, cost: 1 };
      }

      let results: { [key: string]: number } = solver.Solve(model);

      let bestFixedWeight_kg = 0;
      for (let fixedIndex = 0; fixedIndex < fixed.length; fixedIndex++) {
        let fixedVar = `fixed_${fixedIndex}`;
        let fixedWeight_kg = fixed[fixedIndex].weight_kg;
        if (results[fixedVar]) {
          bestFixedWeight_kg = fixedWeight_kg;
          break;
        }
      }

      let bestStackIndex = -1;
      let bestStackWeight_kg = 0;
      if (stack.length > 0) {
        for (let stackIndex = 0; stackIndex < stack.length; stackIndex++) {
          let stackVar = `stack_${stackIndex}`;
          if (results[stackVar]) {
            bestStackWeight_kg = stack[stackIndex].totalWeight_kg;
            bestStackIndex = stackIndex;
            break;
          }
        }
      }

      let setup: WeightSetup = {
        bar_kg: hasABar && hasABar.bar_kg,
        collar_kg: hasABar && hasABar.collar_kg,
        plates: [],
        stackIndex: bestStackIndex,
        fixedWeight_kg: bestFixedWeight_kg,
      };

      let bestPlatesWeight_kg = 0;
      for (let plateWeight in platesMap) {
        let plateVar = `plate_${plateWeight}`;
        let count = results[plateVar];
        if (count) {
          let onePlateWeight_kg = parseFloat(plateWeight);
          for (let i = 0; i < count; i++) {
            setup.plates.push({ weight_kg: onePlateWeight_kg });
            bestPlatesWeight_kg += onePlateWeight_kg * barSidesMul;
          }
        }
      }
      setup.plates.sort((a, b) => b.weight_kg - a.weight_kg);

      let overshoot = results['overshoot'] || 0;
      let undershoot = results['undershoot'] || 0;

      let bestWeight_kg = baseWeight_kg + bestPlatesWeight_kg + bestStackWeight_kg + bestFixedWeight_kg;
      let bestError = undershoot + overshoot;

      if (bestError < hugeError) {
        if (orgMode === 'manual') {
          setup.error_kg = manualSetupError_kg(initialWeight_kg, bestWeight_kg);
          return { weight_kg: initialWeight_kg, setup };
        } else {
          return { weight_kg: bestWeight_kg, setup };
        }
      }
    } else {
      console.assert(false);
    }
  }
  return { weight_kg: initialWeight_kg, setup: null };
}

/**
 * The lightest weight this equipment can actually produce — its floor.
 *
 * Returns undefined for invalid equipment that can produce nothing. Reuses
 * getPossibleWeights(), which returns the producible weights sorted ascending, so the first
 * entry is the minimum. (#3879)
 */
export function getMinProducibleWeight_kg(config: Equipment): number | undefined {
  const possible = getPossibleWeights(config);
  return possible.length > 0 ? possible[0].weight_kg : undefined;
}

/**
 * The equipment "setup error" to surface when reconciling `weight_kg` for display, or
 * undefined when there is nothing actionable to show.
 *
 * A weight that is missing, or below the lightest the equipment can produce, is NOT a setup
 * error — you cannot load lighter than the lightest setting. This is the normal case for a
 * bodyweight exercise done with no added weight (added weight 0) that happens to be set up
 * with loadable equipment, or for a very light exercise on heavier equipment. Only a weight
 * the equipment could match but that lands between increments (at or above the floor) is a
 * real, actionable rounding error worth surfacing. (#3879)
 */
export function getDisplayWeightError_kg(
  weight_kg: number | null | undefined,
  config: Equipment,
): number | undefined {
  if (weight_kg == null || !isFinite(weight_kg)) {
    // no weight to reconcile — nothing to surface
    return undefined;
  }
  const floor_kg = getMinProducibleWeight_kg(config);
  if (floor_kg != null && weight_kg < floor_kg) {
    // below the lightest the equipment can produce — that is the floor, not an actionable
    // error. you cannot load lighter than the lightest setting (e.g. bodyweight-only on a
    // loadable bar), so surfacing a negative "setup error" here is wrong. (#3879)
    return undefined;
  }
  const { setup } = adjustWeight(weight_kg, 'manual', config);
  return setup ? setup.error_kg : undefined;
}

// helper function used to collate plates of same weight into groups with count
export function countSimilarValues(values: number[]): { value: number; count: number }[] {
  // sort the values
  const sortedValues = values.slice().sort((a, b) => a - b);

  // initialize with the first value
  let result = [];
  let lastValue = sortedValues[0];
  let count = 1;

  // iterate through all values after the first
  for (let i = 1; i < sortedValues.length; i++) {
    let value = sortedValues[i];
    // if the value is the same as the last one, increment the count
    if (Math.abs(value - lastValue) < 1e-9) {
      count++;
      // if the value is different, add the last group of values to the result and start a new group
    } else {
      result.push({ value: lastValue, count });
      lastValue = value;
      count = 1;
    }
  }

  // add the last group of values to the result
  if (lastValue !== undefined) {
    result.push({ value: lastValue, count });
  }

  return result;
}

// helper function used to collate weights into groups that are spaced by equal increments
// one group has to have either 1 or at least 3 steps, because it doesn't make sense to have just two
export function countStepValues(values: number[]): { firstValue: number; stepSize: number; numValues: number }[] {
  if (values.length === 0) {
    return [];
  }

  // sort the values
  const sortedValues = values.slice().sort((a, b) => a - b);
  // remove duplicates by skipping similar values that are neighbors
  let uniqueValues: number[] = [];
  let lastValue = sortedValues[0];
  uniqueValues.push(lastValue);
  for (let i = 1; i < sortedValues.length; i++) {
    let value = sortedValues[i];
    if (Math.abs(value - lastValue) > 1e-9) {
      uniqueValues.push(value);
      lastValue = value;
    }
  }

  // for each value
  let result = [];
  for (let i = 0; i < uniqueValues.length;) {
    let firstValue = uniqueValues[i];
    // count how many following ones have the same step
    let stepSize = i + 1 < uniqueValues.length ? uniqueValues[i + 1] - firstValue : 0; // safety for the last value in the array
    let numValues = 1;
    for (let j = i + 1; j < uniqueValues.length; j++) {
      let nextValue = uniqueValues[j];
      let expectedNextValue = firstValue + stepSize * numValues;
      if (Math.abs(nextValue - expectedNextValue) < 1e-4) {
        numValues++;
      } else {
        break;
      }
    }

    // if the step is big enough
    if (numValues >= 3) {
      // store the group
      result.push({ firstValue, stepSize, numValues });
      // and skip all values in the group
      i += numValues;
      // otherwise
    } else {
      // store just this one
      result.push({ firstValue, stepSize: 0, numValues: 1 });
      // and go to the next one
      i++;
    }

  }

  return result;
}

// Generate all combinations of weights that can be done using a set of plates.
function generateCombinations(combiners: { value: number; count: number }[], selection: number[]): { sum: number, selector: number; combiners: number[] }[] {

  function floatToInt(val: number, precision: number) {
    return Math.round(val * precision);
  }

  // compares two combinations to see which is "better"
  function isBetterCombination(aSelector: number, aCombiners: number[], bSelector: number, bCombiners: number[]) {
    // the one with the higher selector weight is better
    if (aSelector > bSelector) {
      return true;
    }
    if (aSelector < bSelector) {
      return false;
    }

    // the one with fewer plates is better
    if (aCombiners.length < bCombiners.length) {
      return true;
    }
    if (aCombiners.length > bCombiners.length) {
      return false;
    }

    // the one with the higher sum of squares is better (which means it uses heavier plates - i.e. prefer 20+10 over 15+15)
    let aSumSq = aCombiners.reduce((sum, val) => sum + val * val, 0);
    let bSumSq = bCombiners.reduce((sum, val) => sum + val * val, 0);
    return aSumSq > bSumSq;
  }


  const precision = 1000;  // Using precision to handle floating point values

  // find the maximum value that can possibly be generated
  const maxCombinerSum = combiners.reduce((sum, combiner) => sum + floatToInt(combiner.value * combiner.count, precision), 0);

  // initialize the dynamic programming array and the combinations
  const dp = Array(maxCombinerSum + 1).fill(false);
  let combos = {};

  dp[0] = true;
  combos[0] = [];

  // for each combiner
  for (const combiner of combiners) {
    const value = floatToInt(combiner.value, precision);
    const count = combiner.count;
    // for each possible sum of weights
    for (let j = maxCombinerSum; j >= 0; j--) {
      // if the sum is already known as possible
      if (dp[j]) {
        // for each possible count in this combiner
        for (let k = 1; k <= count; k++) {
          // if the new sum is possible
          const newSum = j + k * value;
          if (newSum <= maxCombinerSum) {
            // mark it so
            dp[newSum] = true;
            // and store the combination that generated it - if it is shorter than the previous one
            const newCombination = (combos[j] || []).concat(Array(k).fill(combiner.value));
            if (!combos[newSum] || isBetterCombination(0, newCombination, 0, combos[newSum])) {
              combos[newSum] = newCombination;
            }
          }
        }
      }
    }
  }

  // include the case of using 0 weights from combiners
  let combinerResults: number[][] = [];
  for (let i = 0; i <= maxCombinerSum; i++) {
    if (dp[i]) {
      combinerResults[i] = combos[i];
    }
  }

  // for each selector weight
  let finalResults: { [key: number]: { sum: number; selector: number; combiners: number[] } } = {};
  for (const selValue of selection) {
    // for each possible sum of weights
    for (let combinerCombo of combinerResults) {
      if (!combinerCombo) {
        continue;
      }
      let combinerSum = combinerCombo.reduce((sum, val) => sum + val, 0);

      const totalValue = combinerSum + selValue;
      const totalValueInt = floatToInt(totalValue, precision);
      // if shorter than the previous combination
      if (!finalResults[totalValueInt] || isBetterCombination(selValue, combinerCombo, finalResults[totalValueInt].selector, finalResults[totalValueInt].combiners)) {
        // store the combination
        finalResults[totalValueInt] = {
          sum: totalValue,
          selector: selValue,
          combiners: combinerCombo
        };
      }
    }
  }

  return Object.keys(finalResults).map(e => finalResults[e]).sort((a, b) => a.sum - b.sum);
}

/**
Returns a list of all possible weights that can be arranged with the given equipment configuration.

The list is sorted in ascending order of weight.
*/
export function getPossibleWeights(config: Equipment): { weight_kg: number; setup: WeightSetup }[] {
  if (!isEquipmentValid(config)) {
    return [];
  }

  // for manual weights
  if (config.type === EquipmentType.Manual) {
    // we enumerate the first MANUAL_EQUIPMENT_MAX_POSITIONS weights
    let result = [];
    for (let i = 0; i < MANUAL_EQUIPMENT_MAX_POSITIONS; i++) {
      let weight_kg = config.weight_min_kg + i * config.weight_inc_kg;
      result.push({
        weight_kg, setup: {
          plates: [],
          baseWeight_kg: config.weight_min_kg,
          incrementWeight_kg: config.weight_inc_kg,
          increments: i,
        }
      });
    }
    return result;
  }

  // all other types except manual are handled with the common logic
  let baseWeight_kg = 0;
  let bar_kg: number | undefined = undefined;
  let collar_kg: number | undefined = undefined;
  let barSidesMul = 1;
  if (config.type === EquipmentType.Bar) {
    let barConfig = config as EquipmentBar;
    barSidesMul = 2;
    bar_kg = barConfig.bar_kg;
    collar_kg = barConfig.collar_kg;
  }
  baseWeight_kg += (bar_kg || 0) + (collar_kg || 0) * 2;

  // collate all plates of same weight into groups with count
  let plateValues: { value: number; count: number }[] = [];
  if ('plates' in config) {
    plateValues = countSimilarValues(config.plates.map(p => p.weight_kg * barSidesMul));
  }

  // fixed values come from different sources in different configs
  let fixedValues: number[];
  if (config.type === EquipmentType.Bar) {
    fixedValues = [baseWeight_kg];
  } else if (config.type === EquipmentType.Plates) {
    fixedValues = [0];
  } else if (config.type === EquipmentType.Fixed) {
    fixedValues = (config as EquipmentFixed).fixed.map(s => s.weight_kg);
  } else if (config.type === EquipmentType.Stack) {
    fixedValues = (config as EquipmentStack).stack.map(s => s.totalWeight_kg);
  }

  // generate all possible combinations of weights
  let combinations = generateCombinations(plateValues, fixedValues);

  // remap the results into the desired format
  let result = Object.keys(combinations).map(weightKey => {
    let combination = combinations[weightKey];
    let plates = combination.combiners.map(value => ({ weight_kg: value / barSidesMul }));
    let totalWeight_kg = combination.sum;
    if (config.type === EquipmentType.Bar || config.type === EquipmentType.Plates) {
      return {
        weight_kg: totalWeight_kg,
        setup: {
          bar_kg: bar_kg,
          collar_kg: collar_kg,
          plates: plates,
        }
      }
    } else if (config.type === EquipmentType.Stack) {
      return {
        weight_kg: totalWeight_kg,
        setup: {
          stackIndex: config.stack.findIndex(s => s.totalWeight_kg === combination.selector),
          plates: plates,
        }
      };
    } else if (config.type === EquipmentType.Fixed) {
      return {
        weight_kg: totalWeight_kg,
        setup: {
          plates: plates,
          fixedWeight_kg: combination.selector,
        }
      };
    } else {
      console.assert(false);
      return {
        weight_kg: totalWeight_kg, setup: null
      };
    }
  });

  return result.sort((a, b) => a.weight_kg - b.weight_kg);
}
